import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Accordion from './Accordion';
import { toast } from 'react-toastify';
import LoadingSpinner from './LoadingSpinner';
import { TrashIcon } from '@heroicons/react/24/outline';
import CurrencyFormat from './CurrencyFormat';
import { debounce } from 'lodash';

const BuilderEquipmentCp = ({
  titleLeft = 'Items',
  titleRight = 'Selected Items',
  categoryId = 'Equipments'
}) => {
  const { id, key } = useParams(); // numeric ID, e.g. estimate ID
  const navigate = useNavigate();
  const [components, setComponents] = useState([]);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [quantity, setQuantity] = useState({});
  const [openIndex, setOpenIndex] = useState(null);
  const [updateList, setUpdateList] = useState([]);
  const [loading, setLoading] = useState(false);
  const toggleAccordion = useCallback((index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  }, []);

  // Fetch all devices grouped by sub_category for this "categoryId"
  useEffect(() => {
    const fetchDevices = async () => {
      setLoading(true);
      try {
        // The backend expects "equipment_device=true" and "es_builder_estimate_id"
        const response = await axios.get(`${apiUrl}/best_crud.php`, {
          params: {
            estimate: id,
            builder_id: key,
            es_builder_estimate_id: categoryId,
            equipment_device: true,
          },
        });
        console.log(id, key, categoryId, 'params-devices');
        
        console.log(response.data, 'response-devices');
        
        // Expecting ["success", [ { cat: "ANNUNCIATOR", list: [...] }, ... ] ]
        if (response.data[0] === 'success') {
          setComponents(response.data[1] || []);
        } else {
          toast.error(response.data[1] || 'No devices found.', {
            position: 'top-center',
          });
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          navigate('/login');
        } else if (error.response && error.response.status === 403) {
          toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
            position: 'top-center',
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (categoryId) {
      fetchDevices();
    }
  }, [apiUrl, categoryId, navigate]);

  useEffect(() => {
    const fetchRight = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/best_crud.php`, {
          params: {
            estimate: id,
            builder_id: key,
            getDeviceList: id,
          },
        });
        console.log(response.data, 'params-right');
        if (response.data[0] === 'success') {
          const newList = response.data[1] || [];
          setUpdateList(newList);
        }
      } catch (error) {
        if (error.response?.status === 401) navigate('/login');
        if (error.response?.status === 404) navigate(-1);
        if (error.response?.status === 403) toast.error('No access.', { position: 'top-center' });
      } finally {
        setLoading(false);
      }
    };

    fetchRight();
  }, [apiUrl, id, navigate, key]);

  // ADD - Right Column ----------
  const selectedAdd = async (deviceID, qtyVal) => {
    console.log(deviceID, qtyVal, 'selectedAdd');
    
    try {
      const response = await axios.post(`${apiUrl}/best_crud.php`, {
        status: 'addEsDevice',
        estimate_id: id,
        data: { deviceID, quantity: qtyVal },
      });
      console.log(id, key, deviceID, qtyVal, 'params-add');
      console.log(response.data, 'response-add');

      if (response.data[0] === 'success') {
        toast.success('Item added!', { position: 'top-center' });
        const addedItem = response.data[1]; // might contain {id, device, price, quantity,...}

        // Push new item into the updateList
        setUpdateList((prev) => [...prev, addedItem]);

        // Remove from the left column
        setComponents((prevComponents) =>
          prevComponents
            .map((comp) => ({
              ...comp,
              list: comp.list.filter((item) => item.id !== deviceID),
            }))
            .filter((comp) => comp.list.length > 0)
        );
      } else {
        toast.error(response.data.message || 'Failed to add.', { position: 'top-center' });
      }
    } catch (error) {
      toast.error('Error adding item.', { position: 'top-center' });
    }
  };

  // Called when user changes quantity in the right column (local state)
  const handleQuantityChange = debounce((rowId, value) => {
    setUpdateList((prev) =>
      prev.map((item) => (item.id === rowId ? { ...item, quantity: value } : item))
    );
  }, 200);

  // Called when user clicks "Update" in the right column
  const updateQty = async (rowId, qtyVal) => {
    try {
      const response = await axios.post(`${apiUrl}/best_crud.php`, {
        status: 'deviceUpdate',
        data: { id: rowId, quantity: qtyVal },
      });
      console.log(response.data, 'response-update');
      
      if (response.data[0] === 'success') {
        toast.success('Updated', { position: 'top-center' });
        setUpdateList((prev) =>
          prev.map((item) => (item.id === rowId ? { ...item, price: response.data[1] } : item))
        );
      } else {
        toast.error(response.data.message || 'Update failed.', { position: 'top-center' });
      }
    } catch (error) {
      toast.error('Error updating quantity.', { position: 'top-center' });
    }
  };
  // Called when user clicks "Delete" in the right column
  const handleDelete = async (rowId, event) => {
    event.stopPropagation();
    try {
      const response = await axios.post(`${apiUrl}/best_crud.php`, {
        status: 'deleteDevice',
        data: { added_id: rowId },
      });
      if (response.data[0] === 'success') {
        toast.success('Item deleted.', { position: 'top-center' });
        setUpdateList((prev) => prev.filter((item) => item.id !== rowId));
      } else {
        toast.error(response.data.message || 'Failed to delete.', { position: 'top-center' });
      }
    } catch (error) {
      toast.error('Error deleting item.', { position: 'top-center' });
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-x-[2px]">
      {/* LEFT COLUMN */}
      <div>
        <h2 className="text-xl font-bold p-6">{titleLeft}</h2>
        {loading ? (
          <LoadingSpinner>Loading...</LoadingSpinner>
        ) : (
          <div className="max-w-lg mx-auto mt-10">
            {Array.isArray(components) 
              ? components.map((component, index) => (
                  <Accordion
                    key={index}
                    title={component.cat}
                    content={ 
                      Array.isArray(component.list) ? (
                      <div className="divide-y-2 p-2">
                        {component?.list.map((item) => (
                          <div
                            key={item.id}
                            className="grid grid-cols-[55%_15%_15%_15%] gap-2 py-2 items-center"
                          >
                            {/* Device & Description */}
                            <div>
                              <span className="font-bold underline">{item.device}</span>
                              <br />
                              <span className="text-xs">{item.description}</span>
                            </div>
                            {/* Price */}
                            <div>
                              <span>${item.price.toFixed(2)}</span>
                            </div>
                            {/* Quantity Input */}
                            <div>
                              <input
                                type="number"
                                className="border border-gray-300 rounded p-1 text-md w-16"
                                value={quantity[item.id] || ''}
                                onChange={(e) =>
                                  setQuantity({ ...quantity, [item.id]: e.target.value })
                                }
                              />
                            </div>
                            {/* Add Button */}
                            <div>
                              <button
                                className="bg-blue-500 text-white text-md p-2 rounded w-12"
                                onClick={() => selectedAdd(item.id, quantity[item.id])}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      ) : ( <> No devices available</> )}
                    isOpen={openIndex === index}
                    toggle={() => toggleAccordion(index)}
                  />
                ))
              : null
            }
          </div>
        )}
      </div>

      {/* RIGHT COLUMN */}
      <div className="mt-20">
        <h2 className="text-xl font-bold p-6">{titleRight}</h2>
        {loading ? (
          <LoadingSpinner>Loading...</LoadingSpinner>
        ) : (
          <div className="max-w-lg mx-auto mt-9">
            {updateList.map((item) => (
              <div key={item.id}>
                <div className="grid grid-cols-[55%_15%_15%_10%] gap-2 items-center bg-white border rounded-md p-2 mb-2">
                  {/* Device & Price */}
                  <div className="truncate">
                    <span className="text-sm text-gray-900">{item.device}</span>
                    <p className="text-xs text-gray-500">
                      <CurrencyFormat value={item.price} />
                    </p>
                  </div>
                  {/* Quantity */}
                  <div>
                    <input
                      type="number"
                      className="border border-gray-300 rounded p-1 text-md w-16"
                      value={item.quantity || ''}
                      onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                    />
                  </div>
                  {/* Update Button */}
                  <div>
                    <button
                      className="bg-green-500 text-white text-md p-2 rounded w-20"
                      onClick={() => updateQty(item.id, item.quantity)}
                    >
                      Update
                    </button>
                  </div>
                  {/* Delete Button */}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={(e) => handleDelete(item.id, e)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-transparent
                                 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2
                                 focus:ring-indigo-500 focus:ring-offset-2"
                    >
                      <span className="sr-only">Delete item</span>
                      <TrashIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuilderEquipmentCp;
